// 🔐 Hooks de Autenticación con TanStack Query
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { authAPI, esDesafioMfa, type LoginCredentials, type RegisterData, type User } from '../api/auth.api';
import { getErrorMessage, tokenStore } from '../api/client';
import { useTabsStore } from '@/stores/tabs.store';

/**
 * Hook para obtener el usuario actual
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      // Sin tokens guardados no hay sesión: evitamos llamar como anónimo
      if (!tokenStore.getAccess() && !tokenStore.getRefresh()) {
        return null;
      }
      try {
        const u = await authAPI.getCurrentUser();
        // cachea la sesión (por pestaña) para poder ARRANCAR sin red (modo offline del POS)
        // Copia del usuario: por pestaña y, si la sesión se conserva en el dispositivo, también en
        // localStorage → la app reabre SIN SEÑAL sabiendo quién es (campo de TrackConstruction).
        try {
          sessionStorage.setItem('erp_user_cache', JSON.stringify(u));
          if (tokenStore.recordar()) localStorage.setItem('erp_user_cache', JSON.stringify(u)); else localStorage.removeItem('erp_user_cache');
        } catch { /* almacenamiento lleno */ }
        return u;
      } catch (error: any) {
        // 401/403 → no autenticado (normal); limpiamos tokens inválidos
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          tokenStore.clear();
          try { sessionStorage.removeItem('erp_user_cache'); localStorage.removeItem('erp_user_cache'); } catch { /* n/a */ }
          return null;
        }
        // Error de RED (corte de energía/internet): usa la sesión cacheada — el POS
        // opera offline y las llamadas se reintentan cuando vuelva la conexión.
        try {
          const c = sessionStorage.getItem('erp_user_cache') ?? (tokenStore.recordar() ? localStorage.getItem('erp_user_cache') : null);
          if (c) return JSON.parse(c);
        } catch { /* sin caché */ }
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Deja la sesión lista tras un login completo (clave, o clave + MFA).
 * Si entra un usuario distinto al dueño de las pestañas guardadas, se cierran todas.
 */
export function completarSesion(queryClient: QueryClient, user: User | undefined) {
  useTabsStore.getState().ensureOwner(user?.id ?? null);
  queryClient.setQueryData(['currentUser'], { user });
  void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
}

/**
 * Hook para login
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authAPI.login(credentials),
    onSuccess: (data) => {
      // Con desafío MFA todavía no hay sesión: la completa el paso MFA (completarSesion).
      if (esDesafioMfa(data)) return;
      completarSesion(queryClient, data.user);
    },
    onError: (error) => {
      console.error('Error en login:', getErrorMessage(error));
    },
  });
}

/**
 * Hook para register
 */
export function useRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (registerData: RegisterData) => authAPI.register(registerData),
    onSuccess: () => {
      // Invalidar queries después del registro
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (error) => {
      console.error('Error en registro:', getErrorMessage(error));
    },
  });
}

/**
 * Hook para logout
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authAPI.logout,
    onSuccess: () => {
      // Limpiar cache del usuario
      queryClient.setQueryData(['currentUser'], null);
      queryClient.clear();
    },
    onError: (error) => {
      console.error('Error en logout:', getErrorMessage(error));
    },
  });
}

/**
 * Hook combinado para manejar estado de autenticación
 */
export function useAuth() {
  const { data, isLoading, error } = useCurrentUser();
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const registerMutation = useRegister();

  return {
    user: data?.user,
    isLoading,
    isAuthenticated: !!(data && data.user),
    error,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    register: registerMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isRegistering: registerMutation.isPending,
    loginError: loginMutation.error,
    logoutError: logoutMutation.error,
    registerError: registerMutation.error,
  };
}
